/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { StoreFilter } from '@/components/StoreFilter';
import { Repeat, Loader2, Plus, Trash2, RefreshCw, Copy, Ban, TrendingUp, Users, CreditCard, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const SYM = { eur: '€', usd: '$', gbp: '£', pln: 'zł', sek: 'kr' };
const money = (a, c) => `${Number(a || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${SYM[c] || (c || '').toUpperCase()}`;

const STATUS = {
  active: { label: 'Actif', cls: 'bg-emerald-100 text-emerald-700' },
  initiated: { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
  past_due: { label: 'Impayé', cls: 'bg-red-100 text-red-700' },
  completed: { label: 'Terminé', cls: 'bg-zinc-200 text-zinc-700' },
  canceled: { label: 'Annulé', cls: 'bg-zinc-200 text-zinc-500' },
};

const emptyForm = { name: '', description: '', image_url: '', kind: 'subscription', amount: '', interval: 'month', interval_count: 1, total_cycles: 3, store_id: '', vitrine_id: '' };

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider"><Icon className="h-4 w-4" /> {label}</div>
      <div className="text-2xl font-bold mt-2">{value}</div>
      {sub && <div className="text-xs text-zinc-400 mt-1">{sub}</div>}
    </Card>
  );
}

export default function Subscriptions() {
  const [stores, setStores] = useState([]);
  const [vitrines, setVitrines] = useState([]);
  const [storeFilter, setStoreFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/stores').then((r) => setStores(r.data)).catch(() => {});
    api.get('/vitrines').then((r) => setVitrines(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const p = storeFilter ? { store_id: storeFilter } : {};
    try {
      const [s, pl, su] = await Promise.all([
        api.get('/mrr/stats', { params: p }),
        api.get('/plans', { params: p }),
        api.get('/subscriptions', { params: p }),
      ]);
      setStats(s.data); setPlans(pl.data); setSubs(su.data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, [storeFilter]);

  useEffect(() => { load(); }, [load]);

  const vitrineCurrency = (id) => vitrines.find((v) => v.id === id)?.currency || 'eur';

  const createPlan = async () => {
    if (!form.name || !form.amount || !form.vitrine_id) { toast.error('Nom, montant et vitrine requis'); return; }
    setSaving(true);
    try {
      await api.post('/plans', {
        ...form,
        amount: Number(form.amount),
        interval_count: Number(form.interval_count) || 1,
        total_cycles: form.kind === 'installment' ? Number(form.total_cycles) : 0,
      });
      toast.success('Offre créée');
      setOpen(false); setForm(emptyForm); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally { setSaving(false); }
  };

  const togglePlan = async (p) => {
    await api.put(`/plans/${p.id}`, { active: !p.active }).catch(() => {});
    load();
  };
  const deletePlan = async (p) => {
    if (!window.confirm(`Supprimer l'offre « ${p.name} » ? (Les abonnements existants continuent.)`)) return;
    await api.delete(`/plans/${p.id}`).catch(() => {});
    load();
  };
  const copyLink = (p) => {
    const url = `${window.location.origin}/subscribe/${p.id}`;
    navigator.clipboard.writeText(url); toast.success('Lien copié : ' + url);
  };
  const cancelSub = async (s) => {
    if (!window.confirm('Annuler cet abonnement ? Les prélèvements futurs seront arrêtés.')) return;
    await api.post(`/subscriptions/${s.id}/cancel`).catch(() => {});
    load();
  };
  const syncSub = async (s) => {
    await api.post(`/subscriptions/${s.id}/sync`).then(() => toast.success('Actualisé')).catch(() => {});
    load();
  };

  const stripeVitrines = vitrines; // all vitrines (must have a Stripe account to work)

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Repeat className="h-6 w-6" /> Abonnements / MRR</h1>
          <p className="text-sm text-zinc-500">Vends des offres récurrentes ou en plusieurs fois et suis ton revenu mensuel.</p>
        </div>
        <div className="flex items-center gap-2">
          <StoreFilter stores={stores} value={storeFilter} onChange={setStoreFilter} testId="subs-store-filter" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-offer-button"><Plus className="h-4 w-4 mr-1.5" /> Créer une offre</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Nouvelle offre</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="offer-name" placeholder="Box mensuelle Premium" /></div>
                <div><Label className="text-xs">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="offer-desc" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                      <SelectTrigger className="mt-1.5 h-10" data-testid="offer-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="subscription">Abonnement récurrent</SelectItem>
                        <SelectItem value="installment">Paiement en plusieurs fois</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Montant / prélèvement *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="offer-amount" placeholder="29.99" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Périodicité</Label>
                    <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v })}>
                      <SelectTrigger className="mt-1.5 h-10" data-testid="offer-interval"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">Jour</SelectItem>
                        <SelectItem value="week">Semaine</SelectItem>
                        <SelectItem value="month">Mois</SelectItem>
                        <SelectItem value="year">Année</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.kind === 'installment'
                    ? <div><Label className="text-xs">Nombre de prélèvements</Label><Input type="number" min="2" value={form.total_cycles} onChange={(e) => setForm({ ...form, total_cycles: e.target.value })} className="mt-1.5" data-testid="offer-cycles" /></div>
                    : <div><Label className="text-xs">Tous les (x)</Label><Input type="number" min="1" value={form.interval_count} onChange={(e) => setForm({ ...form, interval_count: e.target.value })} className="mt-1.5" data-testid="offer-interval-count" /></div>}
                </div>
                <div>
                  <Label className="text-xs">Vitrine (compte Stripe encaisseur) *</Label>
                  <Select value={form.vitrine_id || undefined} onValueChange={(v) => setForm({ ...form, vitrine_id: v })}>
                    <SelectTrigger className="mt-1.5 h-10" data-testid="offer-vitrine"><SelectValue placeholder="Choisir une vitrine" /></SelectTrigger>
                    <SelectContent>
                      {stripeVitrines.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} · {(v.currency || 'eur').toUpperCase()}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Boutique (optionnel, pour le branding & filtres)</Label>
                  <Select value={form.store_id || '__none__'} onValueChange={(v) => setForm({ ...form, store_id: v === '__none__' ? '' : v })}>
                    <SelectTrigger className="mt-1.5 h-10" data-testid="offer-store"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Aucune</SelectItem>
                      {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.kind === 'installment' && form.amount && (
                  <div className="text-xs text-zinc-500 bg-zinc-50 rounded-md p-2.5">
                    Total : <strong>{money(Number(form.amount) * Number(form.total_cycles || 0), vitrineCurrency(form.vitrine_id))}</strong> en {form.total_cycles} fois.
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={createPlan} disabled={saving} data-testid="offer-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Créer l\'offre'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* MRR stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="MRR" testid="stat-mrr"
          value={stats ? money(stats.mrr, Object.keys(stats.mrr_by_currency || {})[0] || 'eur') : '—'}
          sub={stats && Object.keys(stats.mrr_by_currency || {}).length > 1 ? Object.entries(stats.mrr_by_currency).map(([c, v]) => `${money(v, c)}`).join(' · ') : 'Revenu mensuel récurrent'} />
        <StatCard icon={Users} label="Abonnements actifs" value={stats ? stats.active_subscriptions : '—'} testid="stat-active" />
        <StatCard icon={CreditCard} label="Échéanciers en cours" value={stats ? stats.active_installments : '—'} testid="stat-installments" />
        <StatCard icon={CheckCircle2} label="Terminés / Annulés" value={stats ? `${stats.completed} / ${stats.canceled}` : '—'} testid="stat-ended" />
      </div>

      {/* Offers */}
      <Card className="p-5">
        <div className="font-semibold mb-3">Offres ({plans.length})</div>
        {plans.length === 0 ? <div className="text-sm text-zinc-400 py-4">Aucune offre. Crée ta première offre récurrente ou en plusieurs fois.</div> : (
          <div className="space-y-2">
            {plans.map((p) => (
              <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 border border-zinc-100 rounded-lg p-3" data-testid={`offer-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2 truncate">
                    {p.name}
                    <Badge className={p.active ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-200 text-zinc-500'}>{p.active ? 'Actif' : 'Inactif'}</Badge>
                  </div>
                  <div className="text-xs text-zinc-500">
                    {money(p.amount, p.currency)} {p.kind === 'installment' ? `× ${p.total_cycles} paiements` : `/ ${p.interval}`}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => copyLink(p)} data-testid={`offer-copy-${p.id}`}><Copy className="h-3.5 w-3.5 mr-1" /> Lien</Button>
                  <Button size="sm" variant="outline" onClick={() => togglePlan(p)}>{p.active ? 'Désactiver' : 'Activer'}</Button>
                  <Button size="sm" variant="ghost" onClick={() => deletePlan(p)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Subscribers */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold">Abonnés ({subs.length})</div>
          <Button size="sm" variant="outline" onClick={load} data-testid="subs-refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
        {subs.length === 0 ? <div className="text-sm text-zinc-400 py-4">Aucun abonné pour l'instant.</div> : (
          <>
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-zinc-400 uppercase tracking-wider">
                <th className="py-2">Client</th><th>Offre</th><th>Montant</th><th>Statut</th><th>Cycles</th><th>Prochaine échéance</th><th></th>
              </tr></thead>
              <tbody>
                {subs.map((s) => {
                  const st = STATUS[s.status] || { label: s.status, cls: 'bg-zinc-100 text-zinc-600' };
                  return (
                    <tr key={s.id} className="border-t border-zinc-100" data-testid={`sub-row-${s.id}`}>
                      <td className="py-2.5">
                        <div className="font-medium">{(s.customer?.first_name || '') + ' ' + (s.customer?.last_name || '') || '—'}</div>
                        <div className="text-xs text-zinc-400">{s.customer?.email}</div>
                      </td>
                      <td>{s.plan_name}</td>
                      <td>{money(s.amount, s.currency)}</td>
                      <td><Badge className={st.cls}>{st.label}</Badge></td>
                      <td>{s.kind === 'installment' ? `${s.cycles_paid}/${s.total_cycles}` : (s.cycles_paid || 0)}</td>
                      <td className="text-xs text-zinc-500">{s.current_period_end ? new Date(s.current_period_end).toLocaleDateString('fr-FR') : '—'}</td>
                      <td className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => syncSub(s)} title="Actualiser"><RefreshCw className="h-4 w-4" /></Button>
                        {['active', 'past_due', 'initiated'].includes(s.status) && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => cancelSub(s)} title="Annuler"><Ban className="h-4 w-4" /></Button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-2" data-testid="subs-mobile-list">
            {subs.map((s) => {
              const st = STATUS[s.status] || { label: s.status, cls: 'bg-zinc-100 text-zinc-600' };
              return (
                <div key={s.id} className="border border-zinc-100 rounded-lg p-3" data-testid={`sub-card-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{(s.customer?.first_name || '') + ' ' + (s.customer?.last_name || '') || '—'}</div>
                      <div className="text-xs text-zinc-400 truncate">{s.customer?.email}</div>
                    </div>
                    <Badge className={`${st.cls} shrink-0`}>{st.label}</Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-600">
                    <span className="font-medium text-zinc-800">{s.plan_name}</span>
                    <span className="font-mono">{money(s.amount, s.currency)}</span>
                    <span>{s.kind === 'installment' ? `${s.cycles_paid}/${s.total_cycles} cycles` : `${s.cycles_paid || 0} cycle(s)`}</span>
                    {s.current_period_end && <span>· {new Date(s.current_period_end).toLocaleDateString('fr-FR')}</span>}
                  </div>
                  <div className="mt-2 flex items-center gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => syncSub(s)}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualiser</Button>
                    {['active', 'past_due', 'initiated'].includes(s.status) && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => cancelSub(s)}><Ban className="h-3.5 w-3.5 mr-1" /> Annuler</Button>}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </Card>
    </div>
  );
}
